import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable, take } from 'rxjs';

import {
	Content,
	ContentVisibility,
	CreateFinishMutationResult,
	DeleteFinishesResult,
	EMPTY_CREATE_FINISH_MUTATION_RESULT,
	EMPTY_DELETE_FINISHES_RESULT,
	EMPTY_FINISH_MUTATION_RESULT,
	Finish,
	FinishMutationResult,
	FinishPartial,
	FinishToDependentItem,
	FinishToDocument,
	FinishToSpecification,
} from '@efficiently/models';

import { CheckDeleteFinishOrAreaByIdResponse } from '@efficiently/client-api-finish-id';
import { FinishRepository } from '@efficiently/client-api-project-locations';
import { GraphQLClient } from '@efficiently/client-core-graphql';
import type { DesignChangeCaptureInput } from '@efficiently/client-core-graphql-models';

import {
	CHECK_DELETE_FINISHES,
	CREATE_FINISHES,
	CREATE_FINISHES_WITH_CASCADE,
	DELETE_FINISH_DOCUMENTS,
	DELETE_FINISH_SPECIFICATIONS,
	DELETE_FINISHES,
	DELETE_FINISHES_WITH_CASCADE,
	GET_FINISHES,
	SAVE_FINISH_DEPENDENT_ITEMS,
	SAVE_FINISH_DOCUMENTS,
	SAVE_FINISH_SPECIFICATIONS,
	SET_FINISHID,
	SET_FINISHID_WITH_CASCADE,
	UPDATE_FINISHES,
	UPLOAD_FINISH_DOCUMENTS,
} from '../graphql/queries';

@Injectable()
export class FinishGqlRepository extends FinishRepository {
	private readonly apollo = inject(Apollo);

	public getFinishes(projectId: string): Observable<Finish[]> {
		return this.apollo
			.watchQuery<{ finishes: Finish[] }>({
				query: GET_FINISHES,
				variables: {
					projectId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				map(({ data }) => data.finishes),
			);
	}

	public createFinishes(finishes: Partial<Finish>[], projectId: string): Observable<Finish[]> {
		return this.apollo
			.mutate<{ createFinishes: Finish[] }>({
				mutation: CREATE_FINISHES,
				variables: {
					projectId,
					finishes,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.createFinishes),
			);
	}

	public createFinishesWithCascade(
		finishes: Partial<Finish>[],
		projectId: string,
		designChangeCapture?: DesignChangeCaptureInput,
	): Observable<CreateFinishMutationResult> {
		return this.apollo
			.mutate<{ createFinishesCascade: CreateFinishMutationResult }>({
				mutation: CREATE_FINISHES_WITH_CASCADE,
				variables: {
					projectId,
					finishes,
					designChangeCapture,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data?.createFinishesCascade ?? EMPTY_CREATE_FINISH_MUTATION_RESULT),
			);
	}

	public updateFinishes(finishes: FinishPartial[]): Observable<void> {
		return this.apollo
			.mutate<void>({
				mutation: UPDATE_FINISHES,
				variables: {
					finishes,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data),
			);
	}

	public checkDeleteFinishes(ids: string[]): Observable<CheckDeleteFinishOrAreaByIdResponse> {
		return this.apollo
			.mutate<{ checkDeleteFinishes: CheckDeleteFinishOrAreaByIdResponse }>({
				mutation: CHECK_DELETE_FINISHES,
				variables: {
					ids,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.checkDeleteFinishes),
			);
	}

	public deleteFinishes(ids: string[]): Observable<string[]> {
		return this.apollo
			.mutate<{ deleteFinishes: string[] }>({
				mutation: DELETE_FINISHES,
				variables: {
					ids,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.deleteFinishes),
			);
	}

	public deleteFinishesWithCascade(
		ids: string[],
		designChangeCapture?: DesignChangeCaptureInput,
	): Observable<DeleteFinishesResult> {
		return this.apollo
			.mutate<{ deleteFinishesWithCascade: DeleteFinishesResult }>({
				mutation: DELETE_FINISHES_WITH_CASCADE,
				variables: {
					ids,
					designChangeCapture,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data?.deleteFinishesWithCascade ?? EMPTY_DELETE_FINISHES_RESULT),
			);
	}

	public setFinishId(finishes: string[], finishId: string): Observable<Finish[]> {
		return this.apollo
			.mutate<{ setFinishId: Finish[] }>({
				mutation: SET_FINISHID,
				variables: {
					finishes,
					finishId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.setFinishId),
			);
	}

	public setFinishIdWithCascade(
		finishes: string[],
		finishId: string,
		designChangeCapture?: DesignChangeCaptureInput,
	): Observable<FinishMutationResult> {
		return this.apollo
			.mutate<{ setFinishIdWithCascade: FinishMutationResult }>({
				mutation: SET_FINISHID_WITH_CASCADE,
				variables: {
					finishes,
					finishId,
					designChangeCapture,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data?.setFinishIdWithCascade ?? EMPTY_FINISH_MUTATION_RESULT),
			);
	}

	public uploadFinishDocuments(
		files: File[],
		finishId: string,
		visibility: ContentVisibility = ContentVisibility.Public,
	): Observable<FinishToDocument[]> {
		return this.apollo
			.mutate<{ uploadFinishDocuments: Content[] }>({
				mutation: UPLOAD_FINISH_DOCUMENTS,
				variables: {
					args: {
						files,
						parentId: finishId,
						visibility,
					},
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				map(({ data }) => data.uploadFinishDocuments),
				take(1),
			);
	}

	public deleteFinishDocuments(ids: string[], finishId: string): Observable<string[]> {
		return this.apollo
			.mutate<{ deleteFinishDocuments: string[] }>({
				mutation: DELETE_FINISH_DOCUMENTS,
				variables: {
					ids,
					finishId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				map(({ data }) => data.deleteFinishDocuments),
				take(1),
			);
	}

	public saveFinishDocuments(records: FinishToDocument[]): Observable<FinishToDocument[]> {
		return this.apollo
			.watchQuery<{ saveFinishDocuments: FinishToDocument[] }>({
				query: SAVE_FINISH_DOCUMENTS,
				variables: {
					records,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				map(({ data }) => data?.saveFinishDocuments),
			);
	}

	public saveFinishSpecifications(records: FinishToSpecification[]): Observable<FinishToSpecification[]> {
		return this.apollo
			.watchQuery<{ saveFinishSpecifications: FinishToSpecification[] }>({
				query: SAVE_FINISH_SPECIFICATIONS,
				variables: {
					records,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				map(({ data }) => data?.saveFinishSpecifications),
			);
	}

	public deleteFinishSpecifications(ids: string[], finishId: string): Observable<string[]> {
		return this.apollo
			.mutate<{ deleteFinishSpecifications: string[] }>({
				mutation: DELETE_FINISH_SPECIFICATIONS,
				variables: {
					ids,
					finishId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				map(({ data }) => data.deleteFinishSpecifications),
				take(1),
			);
	}

	public saveFinishDependentItems(records: FinishToDependentItem[]): Observable<FinishToDependentItem[]> {
		return this.apollo
			.mutate<{ saveFinishDependentItems: FinishToDependentItem[] }>({
				mutation: SAVE_FINISH_DEPENDENT_ITEMS,
				variables: {
					records,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.saveFinishDependentItems),
			);
	}
}
